import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveRegistration } from '../services/firestore';
import './Registration.css';

const Registration = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    realLegalStatus: '',
    name: '',
    surname: '',
    phone: '',
    email: '',
    institutionName: '',
    requestType: '',
    eventDate: '',
    passportNumber: '',
    passportIssueDate: '',
    passportExpiryDate: '',
    dateOfBirth: '',
    country: '',
    services: [],
    message: '',
    consentCommercial: false,
    consentPrivacy: false
  });

  const [additionalParticipants, setAdditionalParticipants] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'services') {
        const serviceValue = value;
        setFormData(prev => ({
          ...prev,
          services: prev.services.includes(serviceValue)
            ? prev.services.filter(s => s !== serviceValue)
            : [...prev.services, serviceValue]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const addParticipant = () => {
    setAdditionalParticipants(prev => [
      ...prev,
      { id: Date.now(), name: '', passport: '' }
    ]);
  };

  const removeParticipant = (id) => {
    setAdditionalParticipants(prev => prev.filter(p => p.id !== id));
    // Clear errors for removed participant
    setErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[`participant_${id}_name`];
      delete newErrors[`participant_${id}_passport`];
      return newErrors;
    });
  };

  const handleParticipantChange = (id, field, value) => {
    setAdditionalParticipants(prev =>
      prev.map(p =>
        p.id === id ? { ...p, [field]: value } : p
      )
    );
    // Clear error when user starts typing
    const errorKey = `participant_${id}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.realLegalStatus) {
      newErrors.realLegalStatus = t('registration.pleaseSelectStatus');
    }
    if (!formData.name.trim()) {
      newErrors.name = t('registration.pleaseFillField');
    }
    if (!formData.surname.trim()) {
      newErrors.surname = t('registration.pleaseFillField');
    }
    if (!formData.phone.trim()) {
      newErrors.phone = t('registration.pleaseFillField');
    }
    if (!formData.country.trim()) {
      newErrors.country = t('registration.pleaseFillField');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('registration.pleaseFillField');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('registration.validEmail');
    }
    if (!formData.passportNumber.trim()) {
      newErrors.passportNumber = t('registration.pleaseFillField');
    }
    if (!formData.passportIssueDate) {
      newErrors.passportIssueDate = t('registration.pleaseFillField');
    }
    if (!formData.passportExpiryDate) {
      newErrors.passportExpiryDate = t('registration.pleaseFillField');
    }
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = t('registration.pleaseFillField');
    } else {
      const birthYear = new Date(formData.dateOfBirth).getFullYear();
      if (birthYear > 1955) {
        newErrors.dateOfBirth = 'Date of birth must be on or before 1955';
      }
    }
    if (!formData.requestType) {
      newErrors.requestType = t('registration.pleaseSelectRequestType');
    }
    if (formData.services.length === 0) {
      newErrors.services = t('registration.selectAtLeastOne');
    }
    if (!formData.consentPrivacy) {
      newErrors.consentPrivacy = t('registration.acceptPrivacy');
    }

    // Validate additional participants
    additionalParticipants.forEach((participant) => {
      if (!participant.name.trim()) {
        newErrors[`participant_${participant.id}_name`] = t('registration.pleaseFillField');
      }
      if (!participant.passport.trim()) {
        newErrors[`participant_${participant.id}_passport`] = t('registration.pleaseFillField');
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validate()) {
      setIsSubmitting(true);
      
      try {
      const submissionData = {
        ...formData,
          additionalParticipants: additionalParticipants,
          submittedAt: new Date().toISOString()
      };
        
        const result = await saveRegistration(submissionData);
        
        if (result.success) {
          setSubmitted(true);

          // Reset form after 5 seconds
          setTimeout(() => {
            setSubmitted(false);
            setFormData({
              realLegalStatus: '',
              name: '',
              surname: '',
              phone: '',
              email: '',
              institutionName: '',
              requestType: '',
              eventDate: '',
              passportNumber: '',
              passportIssueDate: '',
              passportExpiryDate: '',
              dateOfBirth: '',
              country: '',
              services: [],
              message: '',
              consentCommercial: false,
              consentPrivacy: false
            });
            setAdditionalParticipants([]);
          }, 5000);
        } else {
          setErrors({ submit: result.error || 'Failed to submit registration. Please try again.' });
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        setErrors({ submit: 'An error occurred. Please try again later.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const serviceOptions = [
    t('registration.service1'),
    t('registration.service2'),
    t('registration.service3'),
    t('registration.service4'),
    t('registration.service5'),
    t('registration.service6'),
    t('registration.service7'),
    t('registration.service8')
  ];

  return (
    <div className="registration-page">
      <div className="container">
        <div className="registration-header glass-card">
          <h2>{t('registration.subtitle')}</h2>
          <p className="registration-description">{t('registration.description')}</p>
        </div>

        {submitted && (
          <div className="success-message glass-card">
            <h3>{t('registration.successTitle')}</h3>
            <p>{t('registration.successMessage')}</p>
          </div>
        )}

        <div className="registration-content">
          <div className="registration-info">
            <div className="info-section glass-card">
              <h3 className="info-section-title">{t('registration.contactInfo')}</h3>
              <div className="info-items-grid">
                <div className="info-item-card">
                  <div className="info-icon-wrapper">
                    <span className="info-icon">📍</span>
                  </div>
                  <div className="info-content-wrapper">
                    <strong>{t('registration.officeLocation')}</strong>
                    <p>Pakistan</p>
                  </div>
                </div>
                <div className="info-item-card">
                  <div className="info-icon-wrapper">
                    <span className="info-icon">✉️</span>
                  </div>
                  <div className="info-content-wrapper">
                    <strong>{t('registration.email')}</strong>
                    <p>info@livestockprofessionals.com</p>
                  </div>
                </div>
                <div className="info-item-card">
                  <div className="info-icon-wrapper">
                    <span className="info-icon">📞</span>
                  </div>
                  <div className="info-content-wrapper">
                    <strong>{t('registration.phone')}</strong>
                    <p>+92 333 3132333</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="info-section glass-card">
              <h3>{t('registration.whyRegister')}</h3>
              <ul>
                <li>✓ {t('registration.benefit1')}</li>
                <li>✓ {t('registration.benefit2')}</li>
                <li>✓ {t('registration.benefit3')}</li>
                <li>✓ {t('registration.benefit4')}</li>
                <li>✓ {t('registration.benefit5')}</li>
              </ul>
            </div>
          </div>

          <div className="registration-form-container glass-card">
            <h2 className="form-title">{t('registration.formTitle')}</h2>
            
            <form onSubmit={handleSubmit} className="registration-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="realLegalStatus">
                    {t('registration.realLegalStatus')} <span className="required">{t('registration.required')}</span>
                  </label>
                  <select
                    id="realLegalStatus"
                    name="realLegalStatus"
                    value={formData.realLegalStatus}
                    onChange={handleChange}
                    className={errors.realLegalStatus ? 'error' : ''}
                  >
                    <option value="">{t('registration.selectStatus')}</option>
                    <option value="individual">{t('registration.individual')}</option>
                    <option value="business">{t('registration.business')}</option>
                    <option value="investor">{t('registration.investor')}</option>
                    <option value="visitor">{t('registration.visitor')}</option>
                  </select>
                  {errors.realLegalStatus && (
                    <span className="error-message">{errors.realLegalStatus}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="requestType">
                    {t('registration.requestType')} <span className="required">{t('registration.required')}</span>
                  </label>
                  <select
                    id="requestType"
                    name="requestType"
                    value={formData.requestType}
                    onChange={handleChange}
                    className={errors.requestType ? 'error' : ''}
                  >
                    <option value="">{t('registration.selectRequestType')}</option>
                    <option value="visa-support">{t('registration.visaSupport')}</option>
                    <option value="event-registration">{t('registration.eventRegistration')}</option>
                    <option value="full-package">{t('registration.fullPackage')}</option>
                    <option value="consultation">{t('registration.consultation')}</option>
                    <option value="other">{t('registration.other')}</option>
                  </select>
                  {errors.requestType && (
                    <span className="error-message">{errors.requestType}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">
                    First Name <span className="required">{t('registration.required')}</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={errors.name ? 'error' : ''}
                    placeholder={t('registration.name')}
                  />
                  {errors.name && (
                    <span className="error-message">{errors.name}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="surname">
                    Last Name <span className="required">{t('registration.required')}</span>
                  </label>
                  <input
                    type="text"
                    id="surname"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    className={errors.surname ? 'error' : ''}
                    placeholder={t('registration.surname')}
                  />
                  {errors.surname && (
                    <span className="error-message">{errors.surname}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="institutionName">
                    {t('registration.institutionName')}
                  </label>
                  <input
                    type="text"
                    id="institutionName"
                    name="institutionName"
                    value={formData.institutionName}
                    onChange={handleChange}
                    placeholder={t('registration.institutionName')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    {t('registration.emailAddress')} <span className="required">{t('registration.required')}</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    placeholder="your.email@example.com"
                  />
                  {errors.email && (
                    <span className="error-message">{errors.email}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="passportNumber">
                    Passport Number <span className="required">{t('registration.required')}</span>
                  </label>
                  <input
                    type="text"
                    id="passportNumber"
                    name="passportNumber"
                    value={formData.passportNumber}
                    onChange={handleChange}
                    className={errors.passportNumber ? 'error' : ''}
                    placeholder="Enter passport number"
                  />
                  {errors.passportNumber && (
                    <span className="error-message">{errors.passportNumber}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="dateOfBirth">
                    Date of Birth <span className="required">{t('registration.required')}</span>
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className={errors.dateOfBirth ? 'error' : ''}
                    max="1955-12-31"
                  />
                  {errors.dateOfBirth && (
                    <span className="error-message">{errors.dateOfBirth}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="passportIssueDate">
                    Passport Issue Date <span className="required">{t('registration.required')}</span>
                  </label>
                  <input
                    type="date"
                    id="passportIssueDate"
                    name="passportIssueDate"
                    value={formData.passportIssueDate}
                    onChange={handleChange}
                    className={errors.passportIssueDate ? 'error' : ''}
                  />
                  {errors.passportIssueDate && (
                    <span className="error-message">{errors.passportIssueDate}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="passportExpiryDate">
                    Passport Expiry Date <span className="required">{t('registration.required')}</span>
                  </label>
                  <input
                    type="date"
                    id="passportExpiryDate"
                    name="passportExpiryDate"
                    value={formData.passportExpiryDate}
                    onChange={handleChange}
                    className={errors.passportExpiryDate ? 'error' : ''}
                  />
                  {errors.passportExpiryDate && (
                    <span className="error-message">{errors.passportExpiryDate}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">
                    {t('registration.phoneNumber')} <span className="required">{t('registration.required')}</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={errors.phone ? 'error' : ''}
                    placeholder="+92 XXX XXXXXXX"
                  />
                  {errors.phone && (
                    <span className="error-message">{errors.phone}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="country">
                    Country <span className="required">{t('registration.required')}</span>
                  </label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className={errors.country ? 'error' : ''}
                    placeholder="Enter your country"
                  />
                  {errors.country && (
                    <span className="error-message">{errors.country}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="eventDate">
                    {t('registration.eventDate')}
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {/* Additional Participants Section */}
              <div className="form-group full-width participants-section">
                <div className="participants-header">
                  <label>
                    {t('registration.additionalParticipants') || 'Additional Participants'}
                  </label>
                  <button
                    type="button"
                    onClick={addParticipant}
                    className="add-participant-btn"
                  >
                    <span>+</span> {t('registration.addParticipant') || 'Add Participant'}
                  </button>
                </div>
                <p className="participants-description">
                  {t('registration.additionalParticipantsDesc') || 'Add other participants who will be attending with you. Include their full name and passport number.'}
                </p>

                {additionalParticipants.length > 0 && (
                  <div className="participants-list">
                    {additionalParticipants.map((participant, index) => (
                      <div key={participant.id} className="participant-card glass-card">
                        <div className="participant-header">
                          <h4>{t('registration.participant') || 'Participant'} {index + 1}</h4>
                          <button
                            type="button"
                            onClick={() => removeParticipant(participant.id)}
                            className="remove-participant-btn"
                            aria-label="Remove participant"
                          >
                            ×
                          </button>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label htmlFor={`participant_${participant.id}_name`}>
                              {t('registration.participantName') || 'Full Name'} <span className="required">*</span>
                            </label>
                            <input
                              type="text"
                              id={`participant_${participant.id}_name`}
                              value={participant.name}
                              onChange={(e) => handleParticipantChange(participant.id, 'name', e.target.value)}
                              className={errors[`participant_${participant.id}_name`] ? 'error' : ''}
                              placeholder={t('registration.participantNamePlaceholder') || 'Enter full name'}
                            />
                            {errors[`participant_${participant.id}_name`] && (
                              <span className="error-message">
                                {errors[`participant_${participant.id}_name`]}
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label htmlFor={`participant_${participant.id}_passport`}>
                              {t('registration.passportNumber') || 'Passport Number'} <span className="required">*</span>
                            </label>
                            <input
                              type="text"
                              id={`participant_${participant.id}_passport`}
                              value={participant.passport}
                              onChange={(e) => handleParticipantChange(participant.id, 'passport', e.target.value)}
                              className={errors[`participant_${participant.id}_passport`] ? 'error' : ''}
                              placeholder={t('registration.passportPlaceholder') || 'Enter passport number'}
                            />
                            {errors[`participant_${participant.id}_passport`] && (
                              <span className="error-message">
                                {errors[`participant_${participant.id}_passport`]}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group full-width">
                <label>
                  {t('registration.servicesRequired')} <span className="required">{t('registration.required')}</span>
                </label>
                <div className="services-checkbox-group">
                  {serviceOptions.map((service, index) => (
                    <label key={index} className="checkbox-label">
                      <input
                        type="checkbox"
                        name="services"
                        value={service}
                        checked={formData.services.includes(service)}
                        onChange={handleChange}
                      />
                      <span>{service}</span>
                    </label>
                  ))}
                </div>
                {errors.services && (
                  <span className="error-message">{errors.services}</span>
                )}
              </div>

              <div className="form-group full-width">
                <label htmlFor="message">
                  {t('registration.specialRequest')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  placeholder={t('registration.specialRequest')}
                />
              </div>

              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="consentCommercial"
                    checked={formData.consentCommercial}
                    onChange={handleChange}
                  />
                  <span>
                    {t('registration.commercialConsent')}
                  </span>
                </label>
              </div>

              <div className="form-group full-width">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="consentPrivacy"
                    checked={formData.consentPrivacy}
                    onChange={handleChange}
                  />
                  <span>
                    {t('registration.privacyConsent')} 
                    <span className="required">{t('registration.required')}</span>
                  </span>
                </label>
                {errors.consentPrivacy && (
                  <span className="error-message">{errors.consentPrivacy}</span>
                )}
              </div>

              {errors.submit && (
                <div className="form-group full-width">
                  <span className="error-message">{errors.submit}</span>
                </div>
              )}

              <button 
                type="submit" 
                className="submit-button btn-modern"
                disabled={isSubmitting}
              >
                {isSubmitting ? t('common.loading') || 'Submitting...' : t('registration.submit')}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;

